import { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Checkbox,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AttachmentPreview from "./AttachmentPreview";
import type { AttachmentDto } from "../api/types";
import { groupAttachmentsByMonth } from "../logic/attachment-grouping";

interface AttachmentSelectModalProps {
  selectionMode?: "single" | "multiple";
  open: boolean;
  onClose: () => void;
  onSave: (selectedIds: string[]) => void;
  availableAttachments: AttachmentDto[];
  initialSelectedIds: string[];
}

export const AttachmentSelectModal = (props: AttachmentSelectModalProps) => {
  // Remount the body each time the modal opens so its state re-initializes
  // from initialSelectedIds (see CLAUDE.md state-management guidance).
  const [instance, setInstance] = useState(0);
  const [prevOpen, setPrevOpen] = useState(props.open);
  const [prevInitial, setPrevInitial] = useState(props.initialSelectedIds);
  if (props.open !== prevOpen) {
    setPrevOpen(props.open);
    if (props.open) setInstance((i) => i + 1);
  }
  if (props.initialSelectedIds !== prevInitial) {
    setPrevInitial(props.initialSelectedIds);
    if (props.open) setInstance((i) => i + 1);
  }
  return <AttachmentSelectModalBody key={instance} {...props} />;
};

const AttachmentSelectModalBody = ({
  open,
  onClose,
  onSave,
  availableAttachments,
  initialSelectedIds,
  selectionMode = "multiple",
}: AttachmentSelectModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);

  const handleToggle = (id: string) => {
    if (selectionMode === "single") {
      setSelectedIds((prev) => (prev.includes(id) ? [] : [id]));
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
      );
    }
  };

  const filteredAndGroupedAttachments = useMemo(() => {
    const filtered = availableAttachments.filter((att) =>
      att.filename.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    return groupAttachmentsByMonth(filtered);
  }, [availableAttachments, searchTerm]);

  const handleSave = () => {
    onSave(selectedIds);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Select Attachments</DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search attachments..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        {filteredAndGroupedAttachments.length === 0 ? (
          <Typography
            align="center"
            sx={{
              color: "text.secondary",
              py: 4,
            }}
          >
            No attachments found.
          </Typography>
        ) : (
          filteredAndGroupedAttachments.map((group) => (
            <Box key={group.month} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
                {group.month}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                {group.attachments.map((att) => {
                  const isSelected = selectedIds.includes(att.id);
                  return (
                    <Card
                      key={att.id}
                      variant="outlined"
                      sx={{
                        width: 140,
                        position: "relative",
                        borderColor: isSelected ? "primary.main" : "divider",
                        borderWidth: isSelected ? 2 : 1,
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleToggle(att.id)}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 1,
                          bgcolor: "background.paper",
                          borderRadius: "50%",
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          // Stop the click (which bubbles to the Card's onClick)
                          // so a checkbox click toggles exactly once.
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggle(att.id)}
                          size="small"
                        />
                      </Box>
                      <CardContent
                        sx={{
                          p: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          "&:last-child": { pb: 1 },
                        }}
                      >
                        {/* We stop propagation here so clicking the preview image opens the large modal and doesn't toggle selection */}
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{ mb: 1 }}
                        >
                          <AttachmentPreview attachment={att} />
                        </Box>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ width: "100%", textAlign: "center" }}
                          title={att.filename}
                        >
                          {att.filename}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Confirm ({selectedIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
